import React, { useEffect, useState } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  Stack,
  Tooltip,
  Typography,
  Alert,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import MemoryIcon from "@mui/icons-material/Memory";
import { GlobalModal } from "../../../components/GlobalModal";
import http from "../../../utils/http-common";

type AnyObj = Record<string, any>;

const shortClass = (fq?: string) =>
  fq && typeof fq === "string" ? fq.split(".").pop() : fq;

const KvChips: React.FC<{ data?: AnyObj }> = ({ data }) => {
  if (!data || typeof data !== "object" || !Object.keys(data).length) return null;
  return (
    <Stack direction="row" spacing={0.75} sx={{ flexWrap: "wrap", gap: 0.75 }}>
      {Object.entries(data).map(([k, v]) => (
        <Chip
          key={k}
          size="small"
          variant="outlined"
          label={
            <Box component="span" sx={{ fontSize: "0.72rem" }}>
              <Box component="span" sx={{ color: "text.secondary" }}>
                {k}:
              </Box>{" "}
              <Box component="span" sx={{ fontWeight: 700 }}>
                {typeof v === "object" ? JSON.stringify(v) : String(v)}
              </Box>
            </Box>
          }
        />
      ))}
    </Stack>
  );
};

interface Props {
  open: boolean;
  onClose: () => void;
  agentId?: number;
  agentTitle?: string;
}

const AgentResourcesModal: React.FC<Props> = ({
  open,
  onClose,
  agentId,
  agentTitle,
}) => {
  const [loading, setLoading] = useState(false);
  const [resources, setResources] = useState<AnyObj[]>([]);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open || agentId == null) return;
    setLoading(true);
    setError("");
    http
      .get(`/agent/${agentId}`)
      .then((res) => {
        const ra = res.data?.meta_data?.resources_available;
        setResources(Array.isArray(ra) ? ra : []);
      })
      .catch(() => setError("Failed to load agent resources."))
      .finally(() => setLoading(false));
  }, [open, agentId]);

  return (
    <GlobalModal
      open={open}
      onClose={onClose}
      title={agentTitle ? `Resources — ${agentTitle}` : "Agent resources"}
      height="auto"
    >
      <Typography color="text.secondary" sx={{ fontSize: "0.85rem", mb: 2, mt: -1 }}>
        What this agent provides — test cases are routed here only if their
        manifest's required resources are all available below.
      </Typography>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : resources.length === 0 ? (
        <Alert severity="info">This agent advertises no resources.</Alert>
      ) : (
        <Stack spacing={1.5}>
          {resources.map((r, i) => {
            const installed = !!r.is_installed;
            return (
              <Box
                key={i}
                sx={{
                  p: 1.75,
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "divider",
                  borderLeft: "3px solid",
                  borderLeftColor: installed ? "#16a34a" : "#9ca3af",
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.75, flexWrap: "wrap" }}>
                  <MemoryIcon fontSize="small" sx={{ color: "primary.main" }} />
                  <Tooltip title={r.target_class || ""}>
                    <Typography sx={{ fontWeight: 800, fontSize: "0.9rem" }}>
                      {shortClass(r.target_class) || "Resource"}
                    </Typography>
                  </Tooltip>
                  {r.attributes?.resource_type && (
                    <Chip size="small" color="primary" variant="outlined" label={r.attributes.resource_type} />
                  )}
                  <Tooltip title={installed ? "Installed / available" : "Not installed"}>
                    {installed ? (
                      <CheckCircleOutlineIcon sx={{ fontSize: 18, color: "#16a34a" }} />
                    ) : (
                      <HighlightOffIcon sx={{ fontSize: 18, color: "#9ca3af" }} />
                    )}
                  </Tooltip>
                  {r.multi_instance && (
                    <Chip size="small" label="multi-instance" variant="outlined" sx={{ height: 20 }} />
                  )}
                </Stack>
                {r.attributes?.manufacturer && (
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                    {r.attributes.manufacturer}
                  </Typography>
                )}
                <KvChips data={r.kwargs} />
              </Box>
            );
          })}
        </Stack>
      )}
    </GlobalModal>
  );
};

export default AgentResourcesModal;
